<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="reveal-overlay"
      :class="{ 'is-mobile': isMobile }"
      @click.self="onBackdropClick"
    >
      <button
        v-if="isMobile"
        type="button"
        class="reveal-close"
        aria-label="Close"
        @click="emitClose"
      >
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>

      <div v-if="isMobile" class="reveal-mobile-stage" @click.stop>
        <p class="reveal-counter">
          CARD {{ activeIndex + 1 }} OF {{ cards.length }}
        </p>
        <div class="reveal-dots" aria-hidden="true">
          <span
            v-for="(_, index) in cards"
            :key="`dot-${index}`"
            class="reveal-dot"
            :class="{ active: index === activeIndex }"
          ></span>
        </div>
        <div class="reveal-stack-wrap">
          <div ref="stackEl" class="reveal-stack">
            <div
              v-for="(card, index) in cards"
              :key="cardKey(card, index)"
              class="reveal-stack-card"
              :class="stackClass(index)"
              @touchstart="onTouchStart($event, index)"
              @touchmove.prevent="onTouchMove($event, index)"
              @touchend="onTouchEnd($event, index)"
              @touchcancel="onTouchEnd($event, index)"
              @click="onCardClick(index)"
            >
              <PlayingCard
                :card="card"
                :face-up="isCardFaceUp(index)"
                :lock-overlay="true"
              />
            </div>
          </div>
        </div>
        <button
          type="button"
          class="reveal-primary"
          :disabled="animating"
          @click="onPrimaryClick"
        >
          {{ isLastCard ? "COLLECT ALL" : "NEXT CARD" }}
        </button>
      </div>

      <div v-else class="reveal-stage" @click.stop>
        <div ref="burstEl" class="burst"></div>
        <h2>Pack Opened</h2>
        <div ref="revealCardsEl" class="reveal-cards">
          <PlayingCard
            v-for="(card, index) in cards"
            :key="cardKey(card, index)"
            class="reveal-card"
            :card="card"
            :face-up="false"
          />
        </div>
        <button type="button" class="reveal-primary" @click="emitClose">
          Done
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { gsap } from "gsap";
import PlayingCard from "./PlayingCard.vue";

const MOBILE_MQ = "(max-width: 767px)";
const FIRST_FLIP_MS = 180;
const DISCARD_MS = 250;
const SWIPE_PX = 50;
const TAP_PX = 10;

const props = defineProps({
  open: { type: Boolean, default: false },
  cards: { type: Array, default: () => [] },
});

const emit = defineEmits(["close"]);

const isMobile = ref(
  typeof window !== "undefined" && window.innerWidth < 768
);
const activeIndex = ref(0);
const animating = ref(false);
const firstFlipped = ref(false);
const discardingIndex = ref(-1);
const discardDir = ref(0);
const burstEl = ref(null);
const revealCardsEl = ref(null);
const stackEl = ref(null);

const touchStartX = ref(0);
const touchStartY = ref(0);
const tracking = ref(false);

let mediaQuery = null;
let firstFlipTimer = null;
let discardTimer = null;
let discardEl = null;
let discardFinishing = false;
let swallowClick = false;
let swallowClickTimer = null;

const isLastCard = computed(
  () =>
    props.cards.length > 0 && activeIndex.value >= props.cards.length - 1
);

function cardKey(card, index) {
  return card?.id != null ? `${card.id}-${index}` : `${card?.slug || "card"}-${index}`;
}

function updateIsMobile() {
  isMobile.value =
    typeof window !== "undefined" && window.innerWidth < 768;
}

function stackClass(index) {
  if (index === discardingIndex.value) {
    return discardDir.value < 0 ? "is-fly-left" : "is-fly-right";
  }
  const offset = index - activeIndex.value;
  if (offset < 0) return "is-gone";
  if (offset === 0) return "is-active";
  if (offset === 1) return "is-plus-1";
  if (offset === 2) return "is-plus-2";
  return "is-buried";
}

function isCardFaceUp(index) {
  if (index > activeIndex.value) return false;
  if (index === 0) return firstFlipped.value;
  return true;
}

function clearTimers() {
  if (firstFlipTimer) {
    clearTimeout(firstFlipTimer);
    firstFlipTimer = null;
  }
  if (discardTimer) {
    clearTimeout(discardTimer);
    discardTimer = null;
  }
  if (swallowClickTimer) {
    clearTimeout(swallowClickTimer);
    swallowClickTimer = null;
  }
  if (discardEl) {
    discardEl.removeEventListener("transitionend", onDiscardTransitionEnd);
    discardEl = null;
  }
}

function resetState() {
  clearTimers();
  activeIndex.value = 0;
  animating.value = false;
  firstFlipped.value = false;
  discardingIndex.value = -1;
  discardDir.value = 0;
  tracking.value = false;
  discardFinishing = false;
  swallowClick = false;
}

function emitClose() {
  clearTimers();
  emit("close");
}

function onBackdropClick() {
  if (isMobile.value && !isLastCard.value) return;
  emitClose();
}

function onPrimaryClick() {
  if (animating.value) return;
  if (isLastCard.value) {
    emitClose();
    return;
  }
  discard(1);
}

function onCardClick(index) {
  if (swallowClick) return;
  if (!isMobile.value || index !== activeIndex.value || animating.value) return;
  if (isLastCard.value) return;
  discard(1);
}

function onTouchStart(event, index) {
  if (!isMobile.value || index !== activeIndex.value || animating.value) return;
  const touch = event.changedTouches?.[0];
  if (!touch) return;
  tracking.value = true;
  touchStartX.value = touch.clientX;
  touchStartY.value = touch.clientY;
}

function onTouchMove(event, index) {
  if (!tracking.value || index !== activeIndex.value) return;
  event.preventDefault();
}

function onTouchEnd(event, index) {
  if (!tracking.value || index !== activeIndex.value) return;
  tracking.value = false;
  swallowClick = true;
  if (swallowClickTimer) clearTimeout(swallowClickTimer);
  swallowClickTimer = setTimeout(() => {
    swallowClick = false;
    swallowClickTimer = null;
  }, 400);
  if (animating.value) return;
  const touch = event.changedTouches?.[0];
  if (!touch) return;
  const dx = touch.clientX - touchStartX.value;
  const dy = touch.clientY - touchStartY.value;
  if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) >= Math.abs(dy)) {
    discard(dx > 0 ? 1 : -1);
    return;
  }
  if (Math.abs(dx) < TAP_PX && Math.abs(dy) < TAP_PX) {
    if (isLastCard.value) return;
    discard(1);
  }
}

function onDiscardTransitionEnd(event) {
  if (event.target !== discardEl) return;
  if (event.propertyName && event.propertyName !== "transform") return;
  finishDiscard();
}

function discard(dir) {
  if (animating.value || !props.open || !props.cards.length) return;
  animating.value = true;
  discardFinishing = false;
  discardingIndex.value = activeIndex.value;
  discardDir.value = dir >= 0 ? 1 : -1;
  nextTick(() => {
    discardEl = stackEl.value?.children?.[discardingIndex.value] || null;
    if (discardEl) {
      discardEl.addEventListener("transitionend", onDiscardTransitionEnd);
    }
    discardTimer = setTimeout(finishDiscard, DISCARD_MS + 30);
  });
}

function finishDiscard() {
  if (!animating.value || discardFinishing) return;
  discardFinishing = true;
  if (discardTimer) {
    clearTimeout(discardTimer);
    discardTimer = null;
  }
  if (discardEl) {
    discardEl.removeEventListener("transitionend", onDiscardTransitionEnd);
    discardEl = null;
  }
  const wasLast = isLastCard.value;
  discardingIndex.value = -1;
  discardDir.value = 0;
  if (wasLast) {
    animating.value = false;
    emitClose();
    return;
  }
  activeIndex.value += 1;
  animating.value = false;
}

async function playRevealAnimation() {
  await nextTick();
  const cards = revealCardsEl.value?.querySelectorAll(".playing-card");
  const inners = revealCardsEl.value?.querySelectorAll(".card-inner");
  if (burstEl.value) {
    gsap.fromTo(
      burstEl.value,
      { scale: 0.2, opacity: 0.9 },
      { scale: 2.4, opacity: 0, duration: 0.7, ease: "power2.out" }
    );
  }
  if (!cards?.length) return;
  gsap.set(cards, { opacity: 0, scale: 0.6 });
  gsap.set(inners, { rotateY: 0 });
  gsap.to(cards, {
    opacity: 1,
    scale: 1,
    duration: 0.35,
    stagger: 0.12,
    ease: "power2.out",
  });
  gsap.to(inners, {
    rotateY: 180,
    duration: 0.7,
    stagger: 0.14,
    delay: 0.25,
    ease: "back.out(1.4)",
  });
}

function startReveal() {
  resetState();
  updateIsMobile();
  nextTick(() => {
    if (isMobile.value) {
      firstFlipTimer = setTimeout(() => {
        firstFlipped.value = true;
      }, FIRST_FLIP_MS);
      return;
    }
    playRevealAnimation();
  });
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) {
      resetState();
      return;
    }
    startReveal();
  }
);

function onMediaChange() {
  updateIsMobile();
}

onMounted(() => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return;
  }
  mediaQuery = window.matchMedia(MOBILE_MQ);
  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", onMediaChange);
  } else if (typeof mediaQuery.addListener === "function") {
    mediaQuery.addListener(onMediaChange);
  }
  updateIsMobile();
  if (props.open) startReveal();
});

onUnmounted(() => {
  resetState();
  if (!mediaQuery) return;
  if (typeof mediaQuery.removeEventListener === "function") {
    mediaQuery.removeEventListener("change", onMediaChange);
  } else if (typeof mediaQuery.removeListener === "function") {
    mediaQuery.removeListener(onMediaChange);
  }
});
</script>

<style scoped>
.reveal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.reveal-overlay.is-mobile {
  height: 100dvh;
  overflow: hidden;
  touch-action: none;
  padding: calc(3.25rem + env(safe-area-inset-top, 0px)) 1rem
    calc(1.25rem + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  background: rgba(4, 10, 20, 0.88);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.reveal-close {
  position: absolute;
  top: calc(1rem + env(safe-area-inset-top, 0px));
  right: 1rem;
  z-index: 1200;
  width: 36px;
  height: 36px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 246, 255, 0.45);
  border-radius: 50%;
  background: rgba(8, 12, 24, 0.9);
  color: #00f6ff;
  cursor: pointer;
}

.reveal-mobile-stage {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 420px;
  pointer-events: auto;
}

.reveal-counter {
  margin: 0 0 0.45rem;
  font-family: "Cinzel", serif;
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  color: rgba(136, 192, 208, 0.9);
}

.reveal-dots {
  display: flex;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
}

.reveal-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(0, 246, 255, 0.22);
  border: 1px solid rgba(0, 246, 255, 0.35);
}

.reveal-dot.active {
  background: #00f6ff;
  box-shadow: 0 0 8px rgba(0, 246, 255, 0.65);
}

.reveal-stack-wrap {
  padding-top: 32px;
  margin-bottom: 1.15rem;
}

.reveal-stack {
  position: relative;
  width: min(75vw, 260px, calc((100dvh - 14rem) * 5 / 7));
  aspect-ratio: 5 / 7;
  touch-action: none;
  overflow: visible;
}

.reveal-stack-card {
  position: absolute;
  inset: 0;
  transform-origin: center center;
  transition:
    transform 0.25s ease,
    opacity 0.25s ease,
    filter 0.25s ease;
  will-change: transform;
}

.reveal-stack-card.is-active {
  z-index: 3;
  transform: translate3d(0, 0, 0) scale(1);
  opacity: 1;
  filter: none;
  pointer-events: auto;
}

.reveal-stack-card.is-plus-1 {
  z-index: 2;
  transform: translate3d(0, -16px, 0) scale(0.94);
  filter: brightness(0.65);
  pointer-events: none;
}

.reveal-stack-card.is-plus-2 {
  z-index: 1;
  transform: translate3d(0, -32px, 0) scale(0.88);
  filter: brightness(0.4);
  pointer-events: none;
}

.reveal-stack-card.is-buried {
  z-index: 0;
  opacity: 0;
  pointer-events: none;
  transform: translate3d(0, -40px, 0) scale(0.84);
}

.reveal-stack-card.is-gone {
  z-index: 0;
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
}

.reveal-stack-card.is-fly-right,
.reveal-stack-card.is-fly-left {
  z-index: 4;
  pointer-events: none;
  filter: none;
  opacity: 0.9;
}

.reveal-stack-card.is-fly-right {
  transform: translateX(120%) rotate(15deg);
}

.reveal-stack-card.is-fly-left {
  transform: translateX(-120%) rotate(-15deg);
}

.reveal-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.reveal-stage {
  position: relative;
  width: 100%;
  max-width: 720px;
  background: #0a0a14;
  border: 1px solid #00f6ff;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 0 30px rgba(0, 246, 255, 0.4);
  text-align: center;
  overflow: hidden;
}

.burst {
  position: absolute;
  left: 50%;
  top: 40%;
  width: 180px;
  height: 180px;
  margin: -90px 0 0 -90px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 204, 102, 0.7), transparent 70%);
  pointer-events: none;
}

.reveal-stage h2 {
  font-family: "Cinzel", serif;
  font-size: 1.2rem;
  margin: 0 0 1.25rem;
  letter-spacing: 1px;
  color: #88c0d0;
  text-align: center;
}

.reveal-cards {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  perspective: 900px;
}

.reveal-card {
  width: 200px;
  flex: 0 0 200px;
}

.reveal-stage .reveal-primary {
  display: inline-block;
  min-width: 0;
  text-transform: none;
  letter-spacing: 0;
  padding: 8px 14px;
}
</style>
